import type { Activity } from '@wyn/content';
import type { MinigameMove, SessionView } from '@wyn/contracts';
import type { InputTrace, RealtimeEngine, TurnEngine } from '@wyn/game-core';
import type { ComponentType } from 'react';
import { SmithingGame } from './SmithingGame.tsx';
import { BrewingGame } from './BrewingGame.tsx';
import { HorsemanshipGame } from './HorsemanshipGame.tsx';
import { LockpickingGame } from './LockpickingGame.tsx';
import { ArcheryGame } from './ArcheryGame.tsx';
import { IlluminationGame } from './IlluminationGame.tsx';
import { JoustingGame } from './JoustingGame.tsx';
import { HuntingGame } from './HuntingGame.tsx';
import { SiegeGame } from './SiegeGame.tsx';
import { CardsGame } from './CardsGame.tsx';
import { AlchemyGame } from './AlchemyGame.tsx';
import { HagglingGame } from './HagglingGame.tsx';
import { DiceGame } from './DiceGame.tsx';
import { FishingGame } from './FishingGame.tsx';
import { MiningGame } from './MiningGame.tsx';
import { WoodcuttingGame } from './WoodcuttingGame.tsx';

/** A real-time game: it runs the simulation and hands back the input trace when the attempt ends. */
export interface LiveGameProps {
  session: SessionView;
  activity: Activity;
  onDone: (trace: InputTrace) => void;
}

/** A turn-based game: it renders the server's view and sends moves. */
export interface TurnGameProps<V> {
  session: SessionView;
  view: V;
  busy: boolean;
  onMove: (m: MinigameMove) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyLive = ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyTurn = ComponentType<TurnGameProps<any>>;

export const LIVE_GAMES: Record<RealtimeEngine, AnyLive> = {
  woodcutting: WoodcuttingGame,
  fishing: FishingGame,
  smithing: SmithingGame,
  brewing: BrewingGame,
  horsemanship: HorsemanshipGame,
  lockpicking: LockpickingGame,
  archery: ArcheryGame,
  illumination: IlluminationGame,
};

export const TURN_GAMES: Record<TurnEngine, AnyTurn> = {
  mining: MiningGame,
  dice: (p) => <DiceGame {...p} commitment={p.session.commitment} />,
  jousting: JoustingGame,
  hunting: HuntingGame,
  siege: SiegeGame,
  cards: CardsGame,
  alchemy: AlchemyGame,
  haggling: HagglingGame,
};

/** How long to leave the final position on screen before the result, per engine. */
export function settleDelayMs(session: SessionView): number {
  if (session.engine === 'dice') return 650 * (session.view.houseRolls.length + 1);
  return 1100;
}
